import BeachAccessOutlinedIcon from "@mui/icons-material/BeachAccessOutlined";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import ManageAccountsOutlinedIcon from "@mui/icons-material/ManageAccountsOutlined";
import { ListItemIcon, ListItemText, Menu, MenuItem, Typography } from "@mui/material";
import type { AutocompleteCloseReason } from "@mui/material/useAutocomplete";
import { Box } from "@mui/system";
import { DateTime } from "luxon";
import type React from "react";
import { Fragment, type SyntheticEvent, useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import AddDayOffDialog from "../../../components/AddDayOffDialog/AddDayOffDialog";
import EditEmployeeStatusDialog from "../../../components/team/EditEmployeeStatusDialog";
import { StaffAutoComplete } from "../../../components/UIToolkit/Inputs/StaffAutoComplete";
import type { SystemAutoCompleteOption } from "../../../components/UIToolkit/Inputs/SystemAutoComplete/SystemAutoComplete";
import { SystemReactTinyPopover } from "../../../components/UIToolkit/Popups/SystemReactTinyPopover";
import SystemButton from "../../../components/UIToolkit/SystemButton/SystemButton";
import { useHospitalData } from "../../../contexts/HospitalDataProvider";
import { useSnackbar } from "../../../contexts/SnackbarContext";
import { useFormatPersonName } from "../../../hooks/useNameFormat";
import { INK, NEUTRAL } from "../../../theme/tokens";
import { getWeekStart } from "../../../utils/dateUtils";
import { isNurseActiveInDepartment } from "../../../utils/formatters";
import type { DayOff, DayOffWithNurseId, Nurse } from "../../../utils/models";
import { nursePreferencesToDaysOffAndPto } from "../../../utils/utilFunctions";
import { useDaysOffAndPtoState } from "../../Profile/v2/DaysOffAndPTO/useDaysOffAndPtoState";
import { useDeleteDayOff } from "../../Profile/v2/hooks";
import { AddPtoButtonRattle } from "./AddPtoButtonRattle";
import { LONG_PTO_BUTTON_HEIGHT_PX } from "./consts";
import { ExistingLongPTOItem } from "./ExistingLongPTOItem";

export const longPtoMinimumDays = 14;

export const LongPTOButton = () => {
	const { t } = useTranslation();
	const { hospitalData, getNurseFormattedName, chosenDepartment, addNewDBNurseShifts, removeNursesShiftsCompletely, getNurseById, updateNurse } =
		useHospitalData();
	const { showSnackbar } = useSnackbar();
	const formatPersonName = useFormatPersonName();
	const [isEditStatusDialogOpen, setIsEditStatusDialogOpen] = useState(false);
	const firstDayOfCurrentWeek = useMemo(() => getWeekStart(DateTime.now(), chosenDepartment.week_start_day), [chosenDepartment.week_start_day]);
	const [isPopoverOpen, setIsPopoverOpen] = useState(false);
	const [selectedNurseForPto, setSelectedNurseForPto] = useState<SystemAutoCompleteOption<Nurse> | null>(null);
	const [isAutoCompleteOpen, setIsAutoCompleteOpen] = useState(false);
	const [timeOffToEdit, setTimeOffToEdit] = useState<DayOffWithNurseId | null>(null);
	const { deleteDayOffMutateAsync, isPending } = useDeleteDayOff({
		addNewDBNurseShifts,
		removeNursesShiftsCompletely,
	});
	const onDeletePtoConfirmed = useCallback(
		async (dayOff: DayOffWithNurseId) => {
			await deleteDayOffMutateAsync({
				dayOff: dayOff,
				nurseId: dayOff.nurseId,
				departmentId: chosenDepartment.department_id,
			});
		},
		[deleteDayOffMutateAsync, chosenDepartment.department_id],
	);
	const { handleSaveNewDayOff } = useDaysOffAndPtoState({
		nurse: selectedNurseForPto?.value,
	});
	const performDayOfDeletion = useCallback(
		async (dayOff: DayOffWithNurseId) => {
			await onDeletePtoConfirmed(dayOff);
			const nurse = getNurseById(dayOff.nurseId);
			if (nurse) {
				updateNurse({
					...nurse,
					employeeSpecificPreferences: nurse.employeeSpecificPreferences.filter((pref) => pref.id !== dayOff.id),
				});
			}
		},
		[onDeletePtoConfirmed, getNurseById, updateNurse],
	);
	const [isAddDayOffDialogOpen, setIsAddDayOffDialogOpen] = useState(false);
	const [actionMenuAnchorEl, setActionMenuAnchorEl] = useState<HTMLElement | null>(null);

	const activeNurses = useMemo(
		() => hospitalData.nurses.filter((nurse) => isNurseActiveInDepartment(nurse, chosenDepartment.department_id)),
		[hospitalData.nurses, chosenDepartment.department_id],
	);

	const nurseListAutoCompleteOptions = useMemo<Array<SystemAutoCompleteOption<Nurse>>>(
		() =>
			activeNurses.map((value) => ({
				value: value,
				label: formatPersonName(value),
			})),
		[activeNurses, formatPersonName],
	);

	const calculatedExistingLongPTOByNurseId = useMemo<Record<number, Array<DayOff>>>(() => {
		const record: Record<number, Array<DayOff>> = {};
		const relevantNurses = selectedNurseForPto ? activeNurses.filter((n) => n.id === selectedNurseForPto.value.id) : activeNurses;
		for (const nurse of relevantNurses) {
			const daysOff = nursePreferencesToDaysOffAndPto(nurse).filter(
				(dayOff) => dayOff.dateTo.diff(dayOff.dateFrom, "days").days >= longPtoMinimumDays && dayOff.dateTo >= firstDayOfCurrentWeek.startOf("day"),
			);
			if (daysOff.length > 0) {
				record[nurse.id] = daysOff;
			}
		}
		return record;
	}, [activeNurses, firstDayOfCurrentWeek, selectedNurseForPto]);

	const handleTogglePopover = useCallback(() => {
		setIsPopoverOpen((prevState) => !prevState);
	}, []);

	const handleClosePopover = useCallback(() => {
		if (isAutoCompleteOpen || isAddDayOffDialogOpen || isEditStatusDialogOpen || timeOffToEdit !== null || actionMenuAnchorEl !== null) return;
		setIsPopoverOpen(false);
	}, [isAutoCompleteOpen, isAddDayOffDialogOpen, isEditStatusDialogOpen, timeOffToEdit, actionMenuAnchorEl]);

	const handleNurseSelectionChanged = useCallback((event: React.SyntheticEvent, value: SystemAutoCompleteOption<Nurse> | null) => {
		event.stopPropagation();
		setSelectedNurseForPto(value);
	}, []);

	const clearSelectedNurse = useCallback(() => {
		setSelectedNurseForPto(null);
		setIsAddDayOffDialogOpen(false);
	}, []);

	const handleAutoCompleteOnOpenStateChange = useCallback((_: SyntheticEvent, __: AutocompleteCloseReason | undefined, isOpen: boolean) => {
		setIsAutoCompleteOpen(isOpen);
	}, []);

	const handleAddIconClick = useCallback(
		(event: React.MouseEvent<HTMLElement>) => {
			if (selectedNurseForPto) {
				setActionMenuAnchorEl(event.currentTarget);
			}
		},
		[selectedNurseForPto],
	);

	const handleActionMenuClose = useCallback(() => {
		setActionMenuAnchorEl(null);
	}, []);

	const handleCreatePtoClick = useCallback(() => {
		setActionMenuAnchorEl(null);
		setIsAddDayOffDialogOpen(true);
	}, []);

	const handleEditStatusClick = useCallback(() => {
		setActionMenuAnchorEl(null);
		setIsEditStatusDialogOpen(true);
	}, []);

	const menuContent = (
		<Box
			sx={{
				px: 1,
			}}
		>
			<Typography
				sx={{
					fontWeight: 700,
					fontSize: "16px",
					lineHeight: "19px",
					color: INK.SECONDARY,
					mb: 2,
				}}
			>
				{t("long.time.off")}
			</Typography>
			<Box
				sx={{
					display: "flex",
					gap: 1,
				}}
			>
				<StaffAutoComplete
					nurses={nurseListAutoCompleteOptions}
					onChange={handleNurseSelectionChanged}
					value={selectedNurseForPto}
					onOpenStateChange={handleAutoCompleteOnOpenStateChange}
				/>
				<AddPtoButtonRattle selectedNurseForPto={selectedNurseForPto} handleAddIconClick={handleAddIconClick} triggerKey={selectedNurseForPto?.value.id ?? 0} />
			</Box>
			<Menu
				anchorEl={actionMenuAnchorEl}
				open={!!actionMenuAnchorEl}
				onClose={handleActionMenuClose}
				data-testid="long-pto-action-menu"
				slotProps={{ list: { dense: true, autoFocusItem: !!actionMenuAnchorEl } }}
			>
				<MenuItem onClick={handleCreatePtoClick} data-testid="long-pto-menu-create-pto">
					<ListItemIcon>
						<BeachAccessOutlinedIcon fontSize="small" />
					</ListItemIcon>
					<ListItemText primary={t("create.new.pto")} />
				</MenuItem>
				<MenuItem onClick={handleEditStatusClick} data-testid="long-pto-menu-edit-status">
					<ListItemIcon>
						<ManageAccountsOutlinedIcon fontSize="small" />
					</ListItemIcon>
					<ListItemText primary={t("employee.status.edit.action")} />
				</MenuItem>
			</Menu>
			{Object.keys(calculatedExistingLongPTOByNurseId).length > 0 && (
				<Box
					sx={{
						display: "flex",
						flexDirection: "row",
						maxWidth: "90vw",
						overflow: "auto",
						mt: 2,
					}}
				>
					{Object.keys(calculatedExistingLongPTOByNurseId)
						.map((nurseId) => Number(nurseId))
						.filter((nurseId) => calculatedExistingLongPTOByNurseId[nurseId] !== undefined)
						.map((nurseId, index) => {
							return (
								<Box
									key={nurseId}
									sx={{
										display: "flex",
										alignItems: "center",
										flexDirection: "row",
										gap: 1,
									}}
								>
									{index > 0 && (
										<Box
											sx={{
												height: "100%",
												width: "1px",
												backgroundColor: NEUTRAL[300],
											}}
										/>
									)}
									<Typography
										sx={{
											fontWeight: 600,
											fontSize: "14px",
											lineHeight: "16px",
											color: INK.SECONDARY,
											whiteSpace: "nowrap",
										}}
									>
										{getNurseFormattedName(nurseId)}
									</Typography>
									{calculatedExistingLongPTOByNurseId[nurseId].map((dayOff) => {
										return (
											<ExistingLongPTOItem
												key={dayOff.id}
												dayOff={{ ...dayOff, nurseId: nurseId }}
												onDeleteConfirmed={performDayOfDeletion}
												isDeleteLoading={isPending}
												timeOffToEdit={timeOffToEdit}
												setTimeOffToEdit={setTimeOffToEdit}
											/>
										);
									})}
								</Box>
							);
						})}
				</Box>
			)}
		</Box>
	);

	return (
		<Fragment>
			<SystemReactTinyPopover positions={["left", "bottom", "right", "top"]} content={menuContent} isOpen={isPopoverOpen} onClose={handleClosePopover}>
				<SystemButton
					sx={{
						py: 0,
						px: 1,
						height: `${LONG_PTO_BUTTON_HEIGHT_PX}px`,
					}}
					buttonVariant={"primary"}
					onClick={handleTogglePopover}
					leadingIcon={<img src="/day-off-white.svg" alt="Day off" width={20} height={25} />}
					data-testid="master-schedule-long-pto-btn"
				>
					{isPopoverOpen ? <ChevronLeftIcon /> : <ChevronRightIcon />}
				</SystemButton>
			</SystemReactTinyPopover>
			{selectedNurseForPto && isAddDayOffDialogOpen && (
				<AddDayOffDialog
					warnLongPtoNotReached={true}
					open={true}
					onClose={clearSelectedNurse}
					onSave={handleSaveNewDayOff}
					nurseId={selectedNurseForPto.value.id}
					longPtoMinimumDaysAmount={longPtoMinimumDays}
				/>
			)}
			{selectedNurseForPto && isEditStatusDialogOpen && (
				<EditEmployeeStatusDialog
					open={true}
					nurse={selectedNurseForPto.value}
					departmentId={chosenDepartment.department_id}
					onClose={() => setIsEditStatusDialogOpen(false)}
					onSaved={() => showSnackbar(t("employee.status.saved"), "success")}
				/>
			)}
		</Fragment>
	);
};
